import React, { useState } from 'react';
import clsx from 'clsx';
import { X, Search, Play, Loader2, Database } from 'lucide-react';
import { useGetContentsQuery, Content } from '../../features/contents/contentsApi';
import { UploadedFile, FileType } from './UploadsPanel';

import { getSafeSignedUrl, getSafeThumbnailUrl } from '../../utils/assetUtils';

interface ContentsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onAddToCanvas?: (file: UploadedFile, dropX?: number, dropY?: number) => void;
}

type FilterTab = 'all' | 'images' | 'videos';

export function ContentsPanel({ isOpen, onClose, onAddToCanvas }: ContentsPanelProps) {
    const [activeTab, setActiveTab] = useState<FilterTab>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    // Debounce search query
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const { data: contentsData, isLoading, isFetching } = useGetContentsQuery({
        search: debouncedSearch || undefined,
        file_type: activeTab === 'all' ? undefined : activeTab === 'images' ? ['image'] : ['video'],
        limit: 20,
        page: page
    });

    const hasMore = contentsData ? contentsData.results.length < contentsData.totalResults : false;

    // Reset page when filters change
    React.useEffect(() => {
        setPage(1);
    }, [activeTab, debouncedSearch]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isFetching) {
            setPage(prev => prev + 1);
        }
    };

    const mapContentToUploadedFile = (content: Content): UploadedFile => {
        const isVideo = content.file_type?.toLowerCase().includes('video');
        const type: FileType = isVideo ? 'video' : 'image';

        const thumbnail = getSafeThumbnailUrl(content, 'px_600x400');
        const fullResUrl = getSafeSignedUrl(content);

        return {
            id: content._id,
            name: content.name,
            type: type,
            thumbnail: thumbnail,
            uploadProgress: 100,
            status: 'completed',
            rawAsset: {
                _id: content._id,
                name: content.name,
                url: fullResUrl, // THIS IS THE FULL QUALITY SIGNED URL
                thumbnail: thumbnail,
                type: type as any,
                file_size: content.file_size || 0,
                createdAt: content.createdAt,
                updatedAt: content.updatedAt
            } as any
        };
    };

    const handleDragStart = (e: React.DragEvent, content: Content) => {
        const isVideo = content.file_type?.toLowerCase().includes('video');
        e.dataTransfer.setData('element-type', isVideo ? 'video' : 'image');
        const uploadedFile = mapContentToUploadedFile(content);
        e.dataTransfer.setData('uploaded-file', JSON.stringify(uploadedFile));
        e.dataTransfer.effectAllowed = 'copy';

        // Create a drag image
        const dragImg = new Image();
        dragImg.src = uploadedFile.thumbnail;
        dragImg.width = 50;
        dragImg.height = 50;
        e.dataTransfer.setDragImage(dragImg, 25, 25);
    };

    return (
        <>


            <div
                className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
            >
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between border-b border-gray-100 bg-white z-10">
                    <div>
                        <h2 className="font-semibold text-gray-900 text-lg">Contents</h2>
                        <p className="text-xs text-gray-500 mt-0.5">External media assets</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-6 py-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search assets..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all"
                        />
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-6 flex gap-1.5 mb-4">
                    {(['all', 'images', 'videos'] as FilterTab[]).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={clsx(
                                "px-3 py-1.5 rounded-lg text-xs font-medium transition-all relative overflow-hidden group capitalize",
                                activeTab === tab
                                    ? "app-active-state"
                                    : "text-gray-600 hover:bg-gray-50"
                            )}
                        >
                            <div 
                                className="app-selector-indicator" 
                                style={{ '--background-width': activeTab === tab ? '100%' : '0%' } as React.CSSProperties} 
                            />
                            <span className="relative z-10 transition-colors duration-400">{tab}</span>
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div
                    className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar"
                    onScroll={handleScroll}
                >
                    {isLoading && !contentsData ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-[#30A84B] animate-spin" />
                            <p className="text-sm text-gray-500 mt-3">Loading contents...</p>
                        </div>
                    ) : contentsData?.results.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <Database className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="font-medium text-gray-900">No assets found</h3>
                            <p className="text-xs text-gray-500 mt-1">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className={clsx("grid grid-cols-2 gap-3 transition-opacity duration-200", isFetching && page === 1 && "opacity-50")}>
                            {contentsData?.results
                                .map((content) => (
                                    <div
                                        key={content._id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, content)}
                                        className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-transparent hover:border-[#30A84B]/30 transition-all shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing"
                                    >
                                        <div className="w-full h-full flex items-center justify-center relative">
                                            {content.file_type.includes('video') ? (
                                                <div className="w-full h-full relative">
                                                    <video
                                                        src={getSafeThumbnailUrl(content, 'px_600x400')}
                                                        className="w-full h-full object-cover rounded-md bg-black"
                                                        muted
                                                        playsInline
                                                        crossOrigin="anonymous"
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                        <div className="bg-black/20 rounded-full p-1.5 backdrop-blur-sm">
                                                            <Play className="w-4 h-4 text-white fill-current" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <img
                                                     src={getSafeThumbnailUrl(content, 'px_600x400')}
                                                    alt={content.name}
                                                    crossOrigin="anonymous"
                                                    className="w-full h-full object-cover transition-opacity duration-300"
                                                    loading="lazy"
                                                />
                                            )}

                                            {/* Hover Actions */}
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAddToCanvas?.(mapContentToUploadedFile(content));
                                                    }}
                                                    className="bg-white text-[#30A84B] p-2 rounded-lg font-semibold text-xs shadow-lg hover:scale-105 transition-transform"
                                                >
                                                    Add to Canvas
                                                </button>
                                            </div>

                                            {/* Type Badge */}
                                            <div className="absolute bottom-1 right-1 bg-black/50 backdrop-blur-md px-1.5 py-0.5 rounded text-[8px] text-white font-bold uppercase tracking-wider">
                                                {content.file_type.split('/')[0]}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}

                    {isFetching && contentsData && (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-6 h-6 text-[#30A84B] animate-spin" />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
